import { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useMyNotifications } from "@/hooks";
import Loader from "@/components/loader/Loader";
import { useMutation } from "@tanstack/react-query";
import { makeRequest } from "@/utils/makeRequest";
import { queryClient } from "@/services/queryClient";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/store/hooks";
import { ErrorState } from "@/components/ErrorState";
import NotificationItem from "@/components/shared/NotificationItem";
import type {
  Notification,
  NotificationInterface,
} from "@/interfaces/notification";

function groupByDate(items: any[] = []) {
  const groups: Record<string, any[]> = {
    Today: [],
    Yesterday: [],
    Earlier: [],
  };

  const now = Date.now();

  for (const n of items) {
    const diff = now - new Date(n.createdAt).getTime();

    if (diff < 86400000) groups.Today.push(n);
    else if (diff < 172800000) groups.Yesterday.push(n);
    else groups.Earlier.push(n);
  }

  return groups;
}

export default function NotificationsPage() {
  const [query, setQuery] = useState("");

  const { user } = useAppSelector((state) => state.auth);

  const userId = user?.id || "";

  const navigate = useNavigate();

  // pagination state
  const [page, setPage] = useState(1);
  const limit = 10;

  const {
    data: notificationsResponse,
    isLoading,
    isError,
    refetch,
  } = useMyNotifications(userId as string, { page, limit });

  // normalize API shape safely
  const notifications = useMemo(() => {
    return notificationsResponse?.data ?? [];
  }, [notificationsResponse]);

  const meta = notificationsResponse?.meta;

  // filtering
  const filtered = useMemo(() => {
    return notifications.filter((n) =>
      `${n.title} ${n.description}`.toLowerCase().includes(query.toLowerCase()),
    );
  }, [notifications, query]);

  const grouped = useMemo(() => groupByDate(filtered), [filtered]);

  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      return makeRequest.put(`/notification/${id}/read`);
    },

    onMutate: async (id: string) => {
      await queryClient.cancelQueries({
        queryKey: ["notifications:me", page, limit, userId],
      });

      const previous = queryClient.getQueryData([
        "notifications:me",
        page,
        limit,
        userId,
      ]);

      queryClient.setQueryData(
        ["notifications:me", page, limit, userId],
        (old: NotificationInterface) => {
          if (!old?.data) return old;

          return {
            ...old,
            data: old.data.map((n: Notification) => {
              if (n.id !== id) return n;

              return {
                ...n,
                recipients: n?.recipients.map((r) => ({
                  ...r,
                  isRead: true,
                  readAt: new Date().toISOString(),
                })),
              };
            }),
          };
        },
      );

      return { previous };
    },

    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(
          ["notifications:me", page, limit, userId],
          ctx.previous,
        );
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications:me", page, limit, userId],
      });
    },
  });

  const markAllAsRead = async () => {
    const unreadIds = notifications
      .filter((n: any) => n.recipients?.some((r: any) => !r.isRead))
      .map((n: any) => n.id);

    for (const id of unreadIds) {
      await markAsReadMutation.mutateAsync(id);
    }

    queryClient.invalidateQueries({ queryKey: ["unread-count", userId] });
  };

  if (isLoading) return <Loader type="bounce" />;

  const unreadCount = notifications.filter((n: any) =>
    n.recipients?.some((r: any) => !r.isRead),
  ).length;

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background lg:px-6">
      <Button
        className="hidden md:flex"
        variant="ghost"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>
      {/* Header */}
      <div className="flex justify-between items-start">
        {/* <div>
          <h1 className="text-xl font-semibold tracking-tight">
            Notification Center
          </h1>
          <p className="text-sm text-muted-foreground">
            Security events, transactions, system updates
          </p>
        </div> */}
        <div className="flex justify-end w-full">
          <Button
            variant="outline"
            size="lg"
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
          >
            Mark all as read
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-2 my-4">
        <Input
          className="h-14"
          placeholder="Search events, trades, alerts..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {/* Timeline Groups */}
      <div className="space-y-8">
        {Object.entries(grouped).map(([label, items]) =>
          items.length ? (
            <div key={label}>
              <h2 className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
                {label}
              </h2>

              <div className="space-y-1 border-l pl-3">
                {items.map((n) => (
                  <NotificationItem
                    key={n.id}
                    n={n}
                    markAsReadMutation={markAsReadMutation}
                    userId={userId}
                  />
                ))}
              </div>
            </div>
          ) : null,
        )}
      </div>

      {/* Pagination */}
      {meta && (
        <div className="flex justify-between items-center pt-4">
          <Button
            variant="outline"
            disabled={!meta.hasPrevPage}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>

          <p className="text-xs text-muted-foreground">
            Page {meta.page} of {meta.totalPages}
          </p>

          <Button
            variant="outline"
            disabled={!meta.hasNextPage}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
